import { api } from './client';
import type { CreditBalance, CreditTransaction, PaginatedResponse } from '@/types';

// Billing Types
export interface BillingConfig {
  publishableKey: string;
}

export interface SubscriptionTier {
  tier: string;
  name: string;
  pricePerMonth: number;
  monthlyCredits: number;
  maxActiveProjects: number;
  maxCustomDomains: number;
  customSslAllowed: boolean;
  prioritySupport: boolean;
  features: string[];
  isPopular: boolean;
}

export interface CreditPack {
  packId: string;
  name: string;
  credits: number;
  bonusCredits: number;
  totalCredits: number;
  price: number;
  pricePerCredit: number;
  savingsPercentage: number;
  isBestValue: boolean;
}

export interface Subscription {
  id: string;
  tier: string;
  tierName: string;
  status: string;
  statusName: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  trialEnd?: string;
  pricePerMonth: number;
  monthlyCredits: number;
}

export interface Invoice {
  id: string;
  invoiceNumber?: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  status: string;
  pdfUrl?: string;
  hostedInvoiceUrl?: string;
  periodStart: string;
  periodEnd: string;
  paidAt?: string;
  createdAt: string;
}

export interface CreditUsageSummary {
  totalUsed: number;
  totalPurchased: number;
  totalBonus: number;
  usageByProject: Record<string, number>;
  usageByDay: Record<string, number>;
}

export interface CheckoutSession {
  sessionId: string;
  url: string;
}

export interface BillingPortalSession {
  url: string;
}

// Billing API
export const billingApi = {
  // Config
  getConfig: () => api.get<BillingConfig>('/api/billing/config'),

  // Tiers & Packs
  getTiers: () => api.get<SubscriptionTier[]>('/api/billing/tiers'),
  getCreditPacks: () => api.get<CreditPack[]>('/api/billing/credit-packs'),

  // Subscription
  getSubscription: () => api.get<Subscription>('/api/billing/subscription'),
  createSubscriptionCheckout: (tier: string) =>
    api.post<CheckoutSession>('/api/billing/checkout/subscription', { tier }),
  cancelSubscription: () => api.post('/api/billing/subscription/cancel'),
  reactivateSubscription: () => api.post('/api/billing/subscription/reactivate'),

  // Credits
  getBalance: () => api.get<CreditBalance>('/api/credits/balance'),
  createCreditPackCheckout: (packId: string) =>
    api.post<CheckoutSession>('/api/billing/checkout/credits', { packId }),

  // Portal
  createBillingPortal: () => api.post<BillingPortalSession>('/api/billing/portal'),

  // Invoices
  getInvoices: (limit = 10) => api.get<Invoice[]>(`/api/billing/invoices?limit=${limit}`),

  // Transactions
  getTransactions: (page = 1, pageSize = 20, type?: string, projectId?: string) => {
    let url = `/api/credits/transactions?page=${page}&pageSize=${pageSize}`;
    if (type) url += `&type=${type}`;
    if (projectId) url += `&projectId=${projectId}`;
    return api.get<PaginatedResponse<CreditTransaction>>(url);
  },

  getUsageSummary: (fromDate?: string, toDate?: string) => {
    let url = '/api/credits/summary';
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    if (params.toString()) url += `?${params.toString()}`;
    return api.get<CreditUsageSummary>(url);
  },
};
